import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Search, Plus, Minus, Trash2, ShoppingCart, Package, Truck, CheckCircle2, Clock,
  PackageCheck, XCircle, Loader2, ArrowRight, ArrowLeft, Download, MapPin, FileText,
  Boxes, AlertTriangle, PackageX, ChevronRight, ClipboardList, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const DEFAULT_THRESHOLD = 15;

// Customer-friendly order lifecycle (Phase 4 statuses; legacy 'pending' => Submitted).
const STATUS = {
  draft: { label: 'Draft', color: '#64748B', icon: ClipboardList },
  submitted: { label: 'Submitted', color: '#F59E0B', icon: Clock },
  pending: { label: 'Submitted', color: '#F59E0B', icon: Clock },
  processing: { label: 'Processing', color: '#3B82F6', icon: Loader2 },
  ready_to_ship: { label: 'Ready to Ship', color: '#6366F1', icon: PackageCheck },
  shipped: { label: 'Shipped', color: '#8B5CF6', icon: Truck },
  delivered: { label: 'Delivered', color: '#10B981', icon: CheckCircle2 },
  completed: { label: 'Delivered', color: '#10B981', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: '#EF4444', icon: XCircle },
};
const st = (s) => STATUS[(s || '').toLowerCase()] || { label: (s || '').replace(/_/g, ' '), color: '#64748B', icon: Clock };
const PROGRESS = ['submitted', 'processing', 'ready_to_ship', 'shipped', 'delivered'];
const normalize = (s) => ((s || '').toLowerCase() === 'pending' ? 'submitted' : (s || '').toLowerCase() === 'completed' ? 'delivered' : (s || '').toLowerCase());

const availability = (p) => {
  // Non-admin roles never receive exact stock; rely on the server-provided availability flag.
  const avail = p?.availability || ((p?.stock_quantity ?? 0) > 0 ? 'available' : 'out_of_stock');
  if (avail === 'out_of_stock') return { key: 'out', label: 'Out of Stock', color: '#EF4444', icon: PackageX };
  return { key: 'in', label: 'Available', color: '#10B981', icon: CheckCircle2 };
};

const StatusPill = ({ status }) => {
  const m = st(status);
  return <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${m.color}1f`, color: m.color }} data-testid="order-status-pill"><m.icon className="w-3 h-3" />{m.label}</span>;
};
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;
const money0 = (n) => money(n);

const downloadPdf = (b64, filename) => {
  try {
    const bytes = atob(b64); const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { toast.error('Could not open document.'); }
};

const ACCOUNT_LABEL = { installation_center: 'Installation Center', elite_partner: 'Elite TC Partner' };

/* =========================== DEALER DASHBOARD =========================== */
export const DealerDashboard = ({ user, setActiveTab }) => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/orders`); setOrders(r.data.orders || r.data || []); }
      catch (e) { setOrders([]); }
    })();
  }, []);

  const counts = useMemo(() => {
    const o = orders || [];
    const by = (k) => o.filter((x) => normalize(x.status) === k).length;
    return { total: o.length, open: by('submitted'), processing: by('processing'), shipped: by('shipped'), delivered: by('delivered') };
  }, [orders]);
  const recent = useMemo(() => [...(orders || [])].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5), [orders]);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="space-y-6" data-testid="dealer-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#14171B] via-[#111418] to-[#0C0E11] px-6 py-7">
        <div className="absolute -right-10 -top-16 w-72 h-72 rounded-full bg-[#C6A15B]/10 blur-3xl pointer-events-none" />
        <p className="text-slate-400 text-sm">{greeting},</p>
        <h2 className="text-2xl sm:text-3xl font-semibold text-white mt-0.5">{user?.company_name || user?.full_name}</h2>
        <p className="text-slate-500 text-sm mt-1">Welcome to your TC Protection Film portal.</p>
        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={() => setActiveTab('create-order')} data-testid="dash-new-order-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium hover:bg-[#d4b36e] transition-colors"><Plus className="w-4 h-4" /> Create New Order</button>
          <button onClick={() => setActiveTab('products')} data-testid="dash-browse-products-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 hover:bg-white/[0.08] transition-colors"><Package className="w-4 h-4" /> Browse Products</button>
        </div>
      </div>

      {/* Order overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <OverviewCard icon={Clock} accent="#F59E0B" label="Submitted" value={counts.open} onClick={() => setActiveTab('orders')} />
        <OverviewCard icon={Loader2} accent="#3B82F6" label="Processing" value={counts.processing} onClick={() => setActiveTab('orders')} />
        <OverviewCard icon={Truck} accent="#8B5CF6" label="Shipped" value={counts.shipped} onClick={() => setActiveTab('orders')} />
        <OverviewCard icon={CheckCircle2} accent="#10B981" label="Delivered" value={counts.delivered} onClick={() => setActiveTab('orders')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent orders */}
        <div className="lg:col-span-2 bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Recent Orders</h3>
            <button onClick={() => setActiveTab('orders')} className="text-[#C6A15B] text-sm hover:underline" data-testid="dash-view-all-orders">View all</button>
          </div>
          {orders === null ? <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>
            : recent.length === 0 ? (
              <div className="text-center py-10">
                <Package className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">No orders yet</p>
                <p className="text-slate-500 text-sm mt-1 mb-4">Place your first TC order to get started.</p>
                <button onClick={() => setActiveTab('create-order')} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium"><Plus className="w-4 h-4" /> Create New Order</button>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.05]">
                {recent.map((o) => (
                  <button key={o.order_id} onClick={() => setActiveTab('orders')} data-testid={`dash-order-${o.order_id}`}
                    className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-white/[0.02] rounded-lg px-1 -mx-1 transition-colors">
                    <div className="min-w-0">
                      <p className="text-slate-100 text-sm font-medium">{o.order_number || `#${(o.order_id || '').slice(0, 8)}`}</p>
                      <p className="text-slate-500 text-xs">{fmtDate(o.created_at)} · {(o.items || []).length} items</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusPill status={o.status} />
                      <span className="text-slate-100 text-sm font-semibold w-20 text-right">{money(o.total_amount)}</span>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </div>
                  </button>
                ))}
              </div>
            )}
        </div>

        {/* Quick actions + account */}
        <div className="space-y-4">
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <QuickAction icon={Plus} label="New Order" onClick={() => setActiveTab('create-order')} testid="qa-new-order" />
              <QuickAction icon={Package} label="Products" onClick={() => setActiveTab('products')} testid="qa-products" />
              <QuickAction icon={ShoppingCart} label="My Orders" onClick={() => setActiveTab('orders')} testid="qa-orders" />
              <QuickAction icon={CheckCircle2} label="Warranty / Certificates" onClick={() => setActiveTab('warranty')} testid="qa-warranty" />
              <QuickAction icon={Boxes} label="Software TC" onClick={() => setActiveTab('software')} testid="qa-software" />
            </div>
          </div>
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
            <h3 className="text-white font-semibold mb-3">Account</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Company</dt><dd className="text-slate-200 text-right truncate">{user?.company_name || '—'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Account Type</dt><dd className="text-[#C6A15B] text-right">{ACCOUNT_LABEL[user?.role] || user?.role}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Contact</dt><dd className="text-slate-200 text-right truncate">{user?.full_name || '—'}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">Email</dt><dd className="text-slate-200 text-right truncate">{user?.email || '—'}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

const OverviewCard = ({ icon: Icon, accent, label, value, onClick }) => (
  <button onClick={onClick} data-testid={`overview-${label.toLowerCase()}`}
    className="text-left bg-[#14171B] border border-white/[0.08] rounded-2xl p-5 hover:border-[#C6A15B]/30 transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-slate-400 text-sm">{label}</span>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}1f`, color: accent }}><Icon className="w-4 h-4" /></span>
    </div>
    <div className="text-2xl font-bold text-white mt-2">{value}</div>
  </button>
);
const QuickAction = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-slate-200 text-sm hover:bg-white/[0.07] transition-colors">
    <Icon className="w-4 h-4 text-[#C6A15B]" /> {label} <ChevronRight className="w-4 h-4 text-slate-600 ml-auto" />
  </button>
);

/* =========================== DEALER NEW ORDER =========================== */
export const DealerNewOrder = ({ user, setActiveTab }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [availFilter, setAvailFilter] = useState('all');
  const [cart, setCart] = useState([]); // {product_id, name, price, qty, stock}
  const [shipping, setShipping] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // created order
  const isElite = user?.role === 'elite_partner';

  useEffect(() => {
    (async () => {
      try {
        const [pr, cr, ar] = await Promise.all([
          axios.get(`${API}/api/products?limit=500`),
          axios.get(`${API}/api/categories`),
          axios.get(`${API}/api/addresses`).catch(() => ({ data: [] })),
        ]);
        setProducts(pr.data.products || pr.data || []);
        setCategories(cr.data || []);
        setAddresses(ar.data || []);
      } catch (e) { toast.error('Error loading products'); }
      finally { setLoading(false); }
    })();
  }, []);

  const catName = (name) => categories.find((c) => c.name === name)?.display_name || name;
  const myPrice = (p) => (typeof p.discounted_price === 'number' ? p.discounted_price : p.price);

  const visible = useMemo(() => {
    let list = [...products];
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter((p) => [p.name, catName(p.category), p.size, p.finish].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)));
    if (catFilter !== 'all') list = list.filter((p) => p.category === catFilter);
    if (availFilter !== 'all') list = list.filter((p) => availability(p).key === availFilter);
    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [products, q, catFilter, availFilter, categories]);

  const addToCart = (p) => {
    if (availability(p).key === 'out') { toast.error('This product is out of stock'); return; }
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.product_id);
      if (ex) return c.map((x) => x.product_id === p.product_id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { product_id: p.product_id, name: p.name, price: myPrice(p), qty: 1 }];
    });
    toast.success(`${p.name} added`);
  };
  const setQty = (pid, qty) => setCart((c) => c.map((x) => x.product_id === pid ? { ...x, qty: Math.max(1, qty) } : x));
  const removeLine = (pid) => setCart((c) => c.filter((x) => x.product_id !== pid));
  const subtotal = cart.reduce((s, x) => s + x.price * x.qty, 0);

  const submit = async () => {
    if (cart.length === 0) { toast.error('Add at least one product'); return; }
    if (!shipping.trim()) { toast.error('Enter a shipping address'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/orders`, {
        items: cart.map((x) => ({ product_id: x.product_id, quantity: x.qty })),
        shipping_address: shipping.trim(),
        special_instructions: notes || null,
        is_draft: false,
      });
      const created = r.data.order || r.data;
      setConfirmation(created);
      setCart([]); setShipping(''); setNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit order'); }
    finally { setSubmitting(false); }
  };

  if (confirmation) {
    const num = confirmation.order_number || `#${(confirmation.order_id || '').slice(0, 8)}`;
    return (
      <div className="max-w-xl mx-auto text-center py-12" data-testid="order-confirmation">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-8 h-8" /></div>
        <h2 className="text-2xl font-bold text-white">Order Submitted</h2>
        <p className="text-slate-400 mt-2">Order <span className="text-[#C6A15B] font-semibold">{num}</span> has been received. Our team will begin processing it shortly.</p>
        <div className="flex flex-wrap gap-3 justify-center mt-6">
          <button onClick={() => setActiveTab('orders')} data-testid="confirm-view-orders" className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium"><ShoppingCart className="w-4 h-4" /> View Orders</button>
          <button onClick={() => setActiveTab('dashboard')} data-testid="confirm-dashboard" className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200"><ArrowLeft className="w-4 h-4" /> Return to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dealer-new-order">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">New Order</h2>
        <p className="text-slate-400 text-sm mt-1">Browse the catalog and build your order with your authorized pricing.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Catalog selection */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className={`${inputCls} pl-9`} data-testid="neworder-search" />
            </div>
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={`${inputCls} sm:w-auto`} data-testid="neworder-category">
              <option value="all">All Categories</option>
              {categories.map((c) => <option key={c.name} value={c.name}>{c.display_name}</option>)}
            </select>
            <select value={availFilter} onChange={(e) => setAvailFilter(e.target.value)} className={`${inputCls} sm:w-auto`} data-testid="neworder-availability">
              <option value="all">All Availability</option>
              <option value="in">Available</option>
              <option value="out">Out of Stock</option>
            </select>
          </div>

          {loading ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}</div>
            : visible.length === 0 ? (
              <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-12 text-center">
                <Boxes className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-300 font-medium">No products found</p>
                <p className="text-slate-500 text-sm">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visible.map((p) => {
                  const av = availability(p);
                  const inCart = cart.find((x) => x.product_id === p.product_id);
                  return (
                    <div key={p.product_id} data-testid={`neworder-product-${p.product_id}`}
                      className="bg-[#14171B] border border-white/[0.08] rounded-xl p-4 flex items-center gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-slate-100 font-medium">{p.name}</h3>
                          <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#C6A15B]/15 text-[#C6A15B]">{catName(p.category)}</span>
                        </div>
                        <div className="text-slate-500 text-xs mt-1">{p.size}{p.finish ? ` · ${p.finish}` : ''}</div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[#C6A15B] font-semibold" data-testid="neworder-price">{money(myPrice(p))}</span>
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${av.color}1f`, color: av.color }}><av.icon className="w-3 h-3" />{av.label}</span>
                        </div>
                      </div>
                      <button disabled={av.key === 'out'} onClick={() => addToCart(p)} data-testid={`neworder-add-${p.product_id}`}
                        className="shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#C6A15B] text-black font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                        <Plus className="w-4 h-4" /> {inCart ? `In cart (${inCart.qty})` : 'Add'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {/* Order summary (sticky) */}
        <div className="lg:sticky lg:top-20 h-fit bg-[#14171B] border border-white/[0.08] rounded-2xl p-5 space-y-4" data-testid="order-summary">
          <h3 className="text-white font-semibold flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-[#C6A15B]" /> Order Summary</h3>
          {cart.length === 0 ? (
            <p className="text-slate-500 text-sm py-6 text-center">Your cart is empty. Add products from the catalog.</p>
          ) : (
            <div className="space-y-3">
              {cart.map((x) => (
                <div key={x.product_id} className="flex items-start gap-2" data-testid={`cart-line-${x.product_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-200 text-sm truncate">{x.name}</p>
                    <p className="text-slate-500 text-xs">{money(x.price)} each</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <button onClick={() => setQty(x.product_id, x.qty - 1)} className="w-6 h-6 rounded bg-white/[0.06] text-slate-300 flex items-center justify-center" data-testid={`cart-dec-${x.product_id}`}><Minus className="w-3 h-3" /></button>
                      <input type="number" min="1" max={x.stock} value={x.qty} onChange={(e) => setQty(x.product_id, parseInt(e.target.value || '1', 10))} className="w-12 bg-black/40 border border-white/10 rounded px-1.5 py-1 text-center text-xs text-slate-100" data-testid={`cart-qty-${x.product_id}`} />
                      <button onClick={() => setQty(x.product_id, x.qty + 1)} className="w-6 h-6 rounded bg-white/[0.06] text-slate-300 flex items-center justify-center" data-testid={`cart-inc-${x.product_id}`}><Plus className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-100 text-sm font-medium">{money(x.price * x.qty)}</p>
                    <button onClick={() => removeLine(x.product_id)} className="text-red-400 text-xs mt-1 inline-flex items-center gap-0.5" data-testid={`cart-remove-${x.product_id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              <div className="border-t border-white/[0.08] pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Subtotal ({cart.reduce((s, x) => s + x.qty, 0)} items)</span><span className="text-slate-200">{money(subtotal)}</span></div>
                <div className="flex justify-between text-base font-semibold"><span className="text-white">Total</span><span className="text-[#C6A15B]" data-testid="summary-total">{money(subtotal)}</span></div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Shipping Address</label>
            {addresses.length > 0 && (
              <select onChange={(e) => e.target.value && setShipping(e.target.value)} className={`${inputCls} mb-2`} defaultValue="" data-testid="neworder-saved-address">
                <option value="">Use a saved address…</option>
                {addresses.map((a) => {
                  const full = [a.full_address, a.city, a.state, a.zip_code].filter(Boolean).join(', ');
                  return <option key={a.address_id || a.address_name} value={full}>{a.address_name ? `${a.address_name} — ${full}` : full}</option>;
                })}
              </select>
            )}
            <textarea rows={2} value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder="Street, city, state, ZIP" className={inputCls} data-testid="neworder-shipping" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Special Instructions <span className="text-slate-600">(optional)</span></label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes for this order" className={inputCls} data-testid="neworder-notes" />
          </div>

          <button onClick={submit} disabled={submitting || cart.length === 0} data-testid="neworder-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#C6A15B] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Submit Order
          </button>
        </div>
      </div>
    </div>
  );
};

/* =========================== DEALER ORDERS =========================== */
export const DealerOrders = ({ user }) => {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/orders`); setOrders(r.data.orders || r.data || []); }
      catch (e) { setOrders([]); }
    })();
  }, []);

  const visible = useMemo(() => {
    let list = [...(orders || [])];
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter((o) => [o.order_number, o.order_id].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)));
    if (statusFilter !== 'all') list = list.filter((o) => normalize(o.status) === statusFilter);
    return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [orders, q, statusFilter]);

  return (
    <div className="space-y-6" data-testid="dealer-orders">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">My Orders</h2>
          <p className="text-slate-400 text-sm mt-1">Track and review all your TC orders.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order number..." className={`${inputCls} pl-9`} data-testid="orders-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} sm:w-auto`} data-testid="orders-status-filter">
          <option value="all">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="processing">Processing</option>
          <option value="ready_to_ship">Ready to Ship</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {orders === null ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
        : visible.length === 0 ? (
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-16 text-center">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">{orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}</p>
            <p className="text-slate-500 text-sm mt-1">{orders.length === 0 ? 'Your submitted orders will appear here.' : 'Try adjusting search or status.'}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-[#14171B] border border-white/[0.08] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/[0.06]">
                    <th className="px-4 py-3 font-medium">Order #</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Items</th>
                    <th className="px-4 py-3 font-medium text-right">Total</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((o) => (
                    <tr key={o.order_id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`order-row-${o.order_id}`}>
                      <td className="px-4 py-3 text-slate-100 font-medium">{o.order_number || `#${(o.order_id || '').slice(0, 8)}`}</td>
                      <td className="px-4 py-3 text-slate-400">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                      <td className="px-4 py-3 text-right text-slate-300">{(o.items || []).length}</td>
                      <td className="px-4 py-3 text-right text-slate-100 font-semibold">{money(o.total_amount)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setDetailId(o.order_id)} data-testid={`view-order-${o.order_id}`} className="inline-flex items-center gap-1 text-[#C6A15B] text-sm hover:underline">View <ChevronRight className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {visible.map((o) => (
                <button key={o.order_id} onClick={() => setDetailId(o.order_id)} data-testid={`order-card-${o.order_id}`}
                  className="w-full text-left bg-[#14171B] border border-white/[0.08] rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-100 font-medium">{o.order_number || `#${(o.order_id || '').slice(0, 8)}`}</span>
                    <StatusPill status={o.status} />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-sm">
                    <span className="text-slate-500">{fmtDate(o.created_at)} · {(o.items || []).length} items</span>
                    <span className="text-slate-100 font-semibold">{money(o.total_amount)}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

      {detailId && <DealerOrderDetail orderId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
};

const DealerOrderDetail = ({ orderId, onClose }) => {
  const [order, setOrder] = useState(null);
  const [dl, setDl] = useState(false);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/orders/${orderId}`); setOrder(r.data.order || r.data); }
      catch (e) { toast.error('Could not load order'); onClose(); }
    })();
  }, [orderId]);

  const invoice = async () => {
    setDl(true);
    try { const r = await axios.get(`${API}/api/orders/${orderId}/invoice`); downloadPdf(r.data.invoice_pdf, `invoice-${(order?.order_number || orderId).toString().replace('#', '')}.pdf`); }
    catch (e) { toast.error('Invoice not available'); }
    finally { setDl(false); }
  };

  const cur = order ? normalize(order.status) : null;
  const cancelled = cur === 'cancelled';
  const curIdx = PROGRESS.indexOf(cur);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="dealer-order-detail">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#14171B] border border-white/[0.08] rounded-2xl max-h-[90vh] overflow-y-auto">
        {!order ? <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div> : (
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] sticky top-0 bg-[#14171B] z-10">
              <div>
                <h3 className="text-white font-semibold">{order.order_number || `#${(order.order_id || '').slice(0, 8)}`}</h3>
                <p className="text-slate-500 text-xs mt-0.5">Placed {fmtDate(order.created_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={order.status} />
                <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Progress */}
              {!cancelled ? (
                <div className="flex items-center justify-between">
                  {PROGRESS.map((step, i) => {
                    const done = curIdx >= i;
                    const m = st(step);
                    return (
                      <React.Fragment key={step}>
                        <div className="flex flex-col items-center gap-1 min-w-0">
                          <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px]" style={{ background: done ? `${m.color}1f` : 'rgba(255,255,255,0.04)', color: done ? m.color : '#64748B' }}>
                            {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                          </span>
                          <span className={`text-[10px] text-center ${done ? 'text-slate-300' : 'text-slate-600'}`}>{m.label}</span>
                        </div>
                        {i < PROGRESS.length - 1 && <div className="flex-1 h-px mx-1" style={{ background: curIdx > i ? st(PROGRESS[i + 1]).color : 'rgba(255,255,255,0.08)' }} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm"><XCircle className="w-4 h-4" /> This order was cancelled.</div>
              )}

              {/* Items */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Items</div>
                <div className="space-y-2">
                  {(order.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 bg-white/[0.02] rounded-lg px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-slate-200 text-sm truncate">{it.product_name}</p>
                        <p className="text-slate-500 text-xs">{it.quantity} × {money(it.effective_unit_price ?? it.unit_price ?? it.price)}</p>
                      </div>
                      <span className="text-slate-100 text-sm font-medium shrink-0">{money(it.effective_total ?? ((it.effective_unit_price ?? it.price ?? 0) * it.quantity))}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-white/[0.08] pt-3 space-y-1.5 text-sm">
                {typeof order.subtotal === 'number' && <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="text-slate-200">{money(order.subtotal)}</span></div>}
                {order.discount_amount > 0 && <div className="flex justify-between"><span className="text-slate-400">Discount{order.discount_percentage ? ` (${order.discount_percentage}%)` : ''}</span><span className="text-emerald-400">−{money(order.discount_amount)}</span></div>}
                <div className="flex justify-between text-base font-semibold"><span className="text-white">Total</span><span className="text-[#C6A15B]">{money(order.total_amount)}</span></div>
              </div>

              {/* Shipping / tracking */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Shipping Address</div>
                  <p className="text-slate-300 whitespace-pre-wrap">{order.shipping_address || '—'}</p>
                </div>
                {(order.carrier || order.tracking_number) && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1"><Truck className="w-3 h-3" /> Tracking</div>
                    <p className="text-slate-300">{order.carrier || 'Carrier'}{order.tracking_number ? ` · ${order.tracking_number}` : ''}</p>
                    {order.ship_date && <p className="text-slate-500 text-xs mt-0.5">Shipped {fmtDate(order.ship_date)}</p>}
                  </div>
                )}
              </div>

              {order.payment_status && (
                <div className="text-sm"><span className="text-slate-500">Payment: </span><span className="text-slate-200 capitalize">{order.payment_status}</span></div>
              )}

              {(order.serial_numbers || []).length > 0 && (
                <div data-testid="dealer-order-serials">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Serial Numbers</div>
                  <div className="flex flex-wrap gap-2">
                    {order.serial_numbers.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-lg bg-white/[0.04] border border-white/10 text-slate-200">{s.serial_number}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={invoice} disabled={dl} data-testid="order-invoice-btn" className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 disabled:opacity-50">
                  {dl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Invoice
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DealerDashboard;
